import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import MaterialIcon from './MaterialIcon';
import TagChip from './TagChip';
import './TagFilter.css';

const TagFilter = ({
  allTags = [],
  selectedTags = [],
  onTagsChange,
  maxTags = 10,
  onGridReverseChange = () => {}, // New prop for grid reverse communication
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isTagListReversed, setIsTagListReversed] = useState(false); // For tag list order
  const [isGridReversed, setIsGridReversed] = useState(false); // For grid order
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter tags based on search term - handle both strings and objects
  const filteredTags = allTags
    .filter((tag) => {
      const tagName = typeof tag === 'string' ? tag : tag?.tag;
      return tagName && tagName.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      // Sort by count - if reversed, flip the order
      const aCount = typeof a === 'string' ? 0 : a?.count || 0;
      const bCount = typeof b === 'string' ? 0 : b?.count || 0;

      if (isTagListReversed) {
        return aCount - bCount; // ASC: least used first
      } else {
        return bCount - aCount; // DESC: most used first (default)
      }
    });

  // Handle tag selection
  const handleTagToggle = (tag) => {
    if (selectedTags.includes(tag)) {
      // Remove tag
      onTagsChange(selectedTags.filter((t) => t !== tag));
    } else {
      // Add tag (if under max limit)
      if (selectedTags.length < maxTags) {
        onTagsChange([...selectedTags, tag]);
      }
    }
  };

  // Handle tag removal from chip
  const handleTagRemove = (tagToRemove) => {
    onTagsChange(selectedTags.filter((tag) => tag !== tagToRemove));
  };

  // Clear all tags
  const handleClearAll = () => {
    onTagsChange([]);
  };

  // Handle grid reverse toggle (main button)
  const handleGridReverseToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newReversed = !isGridReversed;
    setIsGridReversed(newReversed);
    // Communicate back to parent to reverse the grid
    onGridReverseChange(newReversed);
  };

  // Handle tag list reverse toggle (inside dropdown)
  const handleTagListReverseToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTagListReversed(!isTagListReversed);
  };

  // Get button text
  const getButtonText = () => {
    if (selectedTags.length === 0) {
      return 'Filter by Tags';
    }
    return `${selectedTags.length} tag${selectedTags.length === 1 ? '' : 's'} selected`;
  };

  return (
    <div className="tag-filter-container" ref={dropdownRef}>
      {/* Filter Button */}
      <button
        className={`unified-control-button tag-filter-button ${isOpen ? 'active' : ''}`}
        onClick={(e) => {
          // Check if click was on the filter reverse element
          if (!e.target.closest('.filter-reverse-btn')) {
            setIsOpen(!isOpen);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Filter by tags"
      >
        <div
          className={`filter-reverse-btn ${isGridReversed ? 'reversed' : ''} ${selectedTags.length === 0 ? 'disabled' : ''}`}
          onClick={selectedTags.length > 0 ? handleGridReverseToggle : undefined}
          onKeyDown={
            selectedTags.length > 0
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleGridReverseToggle(e);
                  }
                }
              : undefined
          }
          title={
            selectedTags.length === 0
              ? 'Select tags to enable reverse order'
              : isGridReversed
                ? 'Grid order reversed'
                : 'Click to reverse grid order'
          }
          aria-label={
            selectedTags.length === 0
              ? 'Reverse order disabled - select tags first'
              : isGridReversed
                ? 'Restore normal grid order'
                : 'Reverse grid order'
          }
          role="button"
          tabIndex={selectedTags.length > 0 ? 0 : -1}
          aria-disabled={selectedTags.length === 0}
        >
          <MaterialIcon icon="filter_list" size={20} />
        </div>
        <span className="button-text">{getButtonText()}</span>
        <MaterialIcon icon={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} size={20} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="tag-filter-dropdown" role="listbox">
          {/* Search Input */}
          <div className="dropdown-search">
            <MaterialIcon icon="search" size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="dropdown-search-input"
              aria-label="Search tags"
            />
            <button
              className={`dropdown-filter-reverse-btn ${isTagListReversed ? 'reversed' : ''}`}
              onClick={handleTagListReverseToggle}
              title={isTagListReversed ? 'Tags: least used first' : 'Tags: most used first'}
              aria-label={
                isTagListReversed ? 'Show most used tags first' : 'Show least used tags first'
              }
            >
              <MaterialIcon icon="filter_list" size={20} />
            </button>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="clear-search-btn"
                aria-label="Clear search"
              >
                <MaterialIcon icon="close" size={16} />
              </button>
            )}
          </div>

          {/* Tag List */}
          <div className="tag-list" role="listbox">
            {filteredTags.length > 0 ? (
              filteredTags.map((tag) => {
                const tagName = typeof tag === 'string' ? tag : tag?.tag;
                const tagCount = typeof tag === 'string' ? null : tag?.count;
                return (
                  <button
                    key={tagName}
                    className={`tag-option ${selectedTags.includes(tagName) ? 'selected' : ''}`}
                    onClick={() => handleTagToggle(tagName)}
                    role="option"
                    aria-selected={selectedTags.includes(tagName)}
                    disabled={!selectedTags.includes(tagName) && selectedTags.length >= maxTags}
                  >
                    <MaterialIcon
                      icon={
                        selectedTags.includes(tagName) ? 'check_box' : 'check_box_outline_blank'
                      }
                      size={20}
                    />
                    <span className="tag-name">{tagName}</span>
                    {tagCount && <span className="tag-count">({tagCount})</span>}
                  </button>
                );
              })
            ) : (
              <div className="no-results">
                <MaterialIcon icon="search_off" size={24} />
                <span>No tags found</span>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="dropdown-footer">
            {selectedTags.length > 0 && (
              <button
                onClick={handleClearAll}
                className="clear-all-btn"
                aria-label="Clear all selected tags"
              >
                <MaterialIcon icon="clear_all" size={16} />
                <span>Clear All</span>
              </button>
            )}
            {selectedTags.length >= maxTags && (
              <div className="max-limit-warning">
                <MaterialIcon icon="warning" size={16} />
                <span>Maximum {maxTags} tags selected</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

TagFilter.propTypes = {
  allTags: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        tag: PropTypes.string,
        count: PropTypes.number,
      }),
    ])
  ),
  selectedTags: PropTypes.arrayOf(PropTypes.string),
  onTagsChange: PropTypes.func.isRequired,
  maxTags: PropTypes.number,
  onGridReverseChange: PropTypes.func,
};

export default TagFilter;
